import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Divider,  
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
// MUI icons
import SchoolIcon from "@mui/icons-material/School";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import CloudIcon from "@mui/icons-material/Cloud";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SecurityIcon from "@mui/icons-material/Security";
import BugReportIcon from "@mui/icons-material/BugReport";
import SpeedIcon from "@mui/icons-material/Speed";
import InsightsIcon from "@mui/icons-material/Insights";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

type ServiceCard = {
  title: string;
  tag: string;
  icon: React.ReactElement;
  description: string;
  bullets: string[];
  cta: { label: string; to: string };
};

const services: ServiceCard[] = [
  {
    title: "Education Platforms",
    tag: "Learning",
    icon: <SchoolIcon />,
    description:
      "Build web platforms for learning, practice, assessments, and student outcomes—designed for real classrooms and real constraints.",
    bullets: [
      "Student/Teacher/Parent experiences and dashboards",
      "Content, practice journeys, and progress insights",
      "Admin panels, roles, and access controls",
    ],
    cta: { label: "Discuss your platform", to: "/contact-us" },
  },
  {
    title: "Custom Web Development",
    tag: "Frontend + Backend",
    icon: <DeveloperModeIcon />,
    description:
      "Full-stack development with modern frameworks and clean APIs—built for maintainability and long-term ownership.",
    bullets: [
      "React / Angular UI development",
      "Node.js APIs and service layers",
      "TypeScript-first engineering and code quality",
    ],
    cta: { label: "View Technologies", to: "/technologies" },
  },
  {
    title: "Cloud Architecture on AWS",
    tag: "Cloud",
    icon: <CloudIcon />,
    description:
      "Design and deploy cloud-native systems that are secure, scalable, and cost-aware—ideal for education workloads with spiky traffic.",
    bullets: [
      "S3 + CloudFront + Route 53 + ACM for fast HTTPS sites",
      "Security best practices (IAM, least privilege)",
      "Scalable deployments and observability",
    ],
    cta: { label: "Talk to us", to: "/contact-us" },
  },
  {
    title: "Containerization & Kubernetes",
    tag: "DevOps",
    icon: <Inventory2Icon />,
    description:
      "Build and ship reliably with Docker + Kubernetes—repeatable deployments, environment isolation, and scale.",
    bullets: [
      "Docker images and multi-env builds",
      "Kubernetes deployment patterns",
      "Release automation and rollback strategies",
    ],
    cta: { label: "Plan delivery", to: "/contact-us" },
  },
  {
    title: "Security & Performance",
    tag: "Reliability",
    icon: <SecurityIcon />,
    description:
      "Make your platform safer and faster through practical improvements and measurable outcomes.",
    bullets: [
      "HTTPS, security headers, access control reviews",
      "Caching strategy (Redis / CDN) for performance",
      "Load testing readiness and tuning",
    ],
    cta: { label: "Request review", to: "/contact-us" },
  },
  {
    title: "AI Features & Assistants",
    tag: "AI",
    icon: <AutoAwesomeIcon />,
    description:
      "Integrate AI thoughtfully into your product to improve learning outcomes and productivity—without turning your product into a demo.",
    bullets: [
      "Knowledge Q&A (RAG) for help and support",
      "Summaries, suggestions, and smart workflows",
      "Guardrails and safe AI usage patterns",
    ],
    cta: { label: "Explore AI options", to: "/contact-us" },
  },
];

const engagementModels = [
  {
    title: "MVP Delivery",
    icon: <ArchitectureIcon />,
    desc: "Define scope, build core flows fast, ship, and iterate with real users.",
  },
  {
    title: "Dedicated Team",
    icon: <InsightsIcon />,
    desc: "A focused team aligned to your roadmap with predictable delivery cadence.",
  },
  {
    title: "Modernization",
    icon: <SpeedIcon />,
    desc: "Improve performance, security, and reliability without full rewrites.",
  },
];

const qualityPillars = [
  { title: "Testing", icon: <BugReportIcon />, desc: "Unit + integration + E2E coverage aligned to risk." },
  { title: "Reviews", icon: <ArchitectureIcon />, desc: "Code reviews, conventions, and maintainability checks." },
  { title: "CI/CD", icon: <Inventory2Icon />, desc: "Automated builds, deployments, and consistent environments." },
  { title: "Monitoring", icon: <CloudIcon />, desc: "Logging, metrics, and alerts to reduce production surprises." },
];

const Services = () => {
  return (
    <Box component="main">
      {/* HERO */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: 980, mx: "auto" }}>
            <Typography variant="h4" component="h1" fontWeight={900} gutterBottom>
              Services
            </Typography>
            <Typography variant="h6" color="text.secondary">
              We help education-focused teams build, launch, and scale software products—using modern
              web development and cloud-native delivery.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
              <Button variant="contained" component={RouterLink} to="/contact-us">
                Get a proposal
              </Button>
              <Button variant="outlined" component={RouterLink} to="/products">
                View Products
              </Button>
              <Button variant="outlined" component={RouterLink} to="/technologies">
                Our Technologies
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* SERVICES GRID */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            What we can build for you
          </Typography>
          <Typography color="text.secondary">
            Clear deliverables, practical engineering, and a focus on education outcomes.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {services.map((s) => (
            <Grid item xs={12} md={4} key={s.title}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center" }}>{s.icon}</Box>
                    <Typography variant="h6" fontWeight={800} sx={{ flexGrow: 1 }}>
                      {s.title}
                    </Typography>
                    <Chip size="small" label={s.tag} />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {s.description}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={1}>
                    {s.bullets.map((b) => (
                      <Typography key={b} variant="body2">
                        • {b}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button variant="contained" component={RouterLink} to={s.cta.to}>
                    {s.cta.label}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ENGAGEMENT MODELS */}
      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: "action.hover" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
              Engagement models
            </Typography>
            <Typography color="text.secondary">
              Choose what fits your roadmap, team, and timeline.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {engagementModels.map((m) => (
              <Grid item xs={12} md={4} key={m.title}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center" }}>{m.icon}</Box>
                      <Typography variant="h6" fontWeight={800}>
                        {m.title}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {m.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* QUALITY STRIP */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Delivery, testing & quality
          </Typography>
          <Typography color="text.secondary">
            We ship fast with quality gates that reduce regressions and production risk.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {qualityPillars.map((p) => (
            <Grid item xs={12} md={3} key={p.title}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center" }}>{p.icon}</Box>
                    <Typography variant="h6" fontWeight={800}>
                      {p.title}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {p.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            mt: 5,
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
        >
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Let’s build your education platform.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Share your goals and we’ll propose an MVP plan, architecture, and delivery approach.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button variant="contained" size="large" component={RouterLink} to="/contact-us">
              Contact Us
            </Button>
            <Button variant="outlined" size="large" component={RouterLink} to="/technologies">
              See Technologies
            </Button>
            <Button variant="outlined" size="large" component={RouterLink} to="/products">
              View Products
            </Button>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
};

export default Services;
